import * as React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  Banknote,
  CheckSquare,
  FileText,
  LayoutDashboard,
  Menu,
  Receipt,
  ScrollText,
  ShieldCheck,
  Users
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { ProfileMenu } from "@/components/ProfileMenu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  SidebarTrigger,
  useSidebar
} from "@/components/ui/sidebar";
import { consumeBackGuardForNavigation, useBackTarget } from "@/lib/navigation";
import { cn } from "@/lib/utils";
import { AuthUser } from "../types";

type NavEntry = {
  to: string;
  end?: boolean;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
};

const PRIMARY_ITEMS: NavEntry[] = [
  { to: "/", end: true, icon: LayoutDashboard, label: "Dashboard" }
];

const MY_WORK_ITEMS = (isManager: boolean): NavEntry[] => [
  { to: "/my-requests", icon: FileText, label: "Travel Reimbursements" },
  // Deliberately not under /my-requests, so isActivePath (a startsWith
  // match) never highlights both items.
  { to: "/other-reimbursements", icon: Receipt, label: "Other Reimbursements" },
  ...(isManager
    ? [{ to: "/my-approvals", icon: CheckSquare, label: "My Approvals" }]
    : [])
];

const ADMIN_ITEMS: NavEntry[] = [
  { to: "/admin/users", icon: Users, label: "User Management" },
  { to: "/admin/policies", icon: ScrollText, label: "Policies" },
  { to: "/admin/audit-logs", icon: ShieldCheck, label: "Audit Logs" }
];

const APPROVER_ITEMS: NavEntry[] = [
  { to: "/approver-queue", icon: Banknote, label: "Approver Queue" }
];

function isActivePath(pathname: string, item: NavEntry): boolean {
  return item.end ? pathname === item.to : pathname.startsWith(item.to);
}

function NavGroup({ label, items }: { label?: string; items: NavEntry[] }) {
  const location = useLocation();
  const { isMobile, setOpenMobile } = useSidebar();
  return (
    <SidebarGroup>
      {label && <SidebarGroupLabel>{label}</SidebarGroupLabel>}
      <SidebarGroupContent>
        <SidebarMenu>
          {items.map((item) => {
            const isActive = isActivePath(location.pathname, item);
            return (
              <SidebarMenuItem key={item.to}>
                <SidebarMenuButton
                  asChild
                  isActive={isActive}
                  tooltip={item.label}
                >
                  {/* On phones the sidebar is a Sheet drawer (opened from
                     the bottom bar's "More" tab) - close it on navigation
                     so the chosen page is actually visible.
                     consumeBackGuardForNavigation must run before
                     setOpenMobile(false) here - without it, closing the
                     drawer raced this Link's own navigation and silently
                     cancelled it (tapping a drawer item just closed the
                     drawer with no page change); see its docstring in
                     lib/navigation.ts for why. */}
                  <NavLink
                    to={item.to}
                    end={item.end}
                    onClick={() => {
                      if (!isMobile) return;
                      consumeBackGuardForNavigation();
                      setOpenMobile(false);
                    }}
                  >
                    <item.icon />
                    <span>{item.label}</span>
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            );
          })}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}

/** The app's sidebar, built on shadcn/ui's real Sidebar primitives (not a
 *  hand-rolled <aside>) - this is what gives us collapse-to-icon mode,
 *  automatic mobile Sheet behaviour, per-item tooltips when collapsed, and
 *  the ⌘/Ctrl+B shortcut for free, all themed off the same index.css tokens
 *  as every other component. See CLAUDE.md: never hand-edit components/ui/*,
 *  the CLI owns those files. */
export default function AppSidebar({ user }: { user: AuthUser }) {
  const isAdmin = !!user.roles?.includes("Admin");
  const isFinance = !!user.roles?.includes("Finance");
  const isManager = !!user.isManager;

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <div className="flex items-center gap-2.5 px-1 py-1 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0">
          <img
            src="/asmara-logo.png"
            alt="ASMARA"
            className="h-8 w-8 shrink-0 object-contain"
          />
          <div className="min-w-0 leading-tight group-data-[collapsible=icon]:hidden">
            <p className="truncate text-sm font-semibold tracking-tight">
              ASMARA
            </p>
            <p className="truncate text-[0.6875rem] text-muted-foreground">
              Expense Reimbursement
            </p>
          </div>
        </div>
      </SidebarHeader>

      <SidebarContent>
        <NavGroup items={PRIMARY_ITEMS} />
        <NavGroup label="My Work" items={MY_WORK_ITEMS(isManager)} />
        {isFinance && (
          <NavGroup label="Finance Approver" items={APPROVER_ITEMS} />
        )}
      </SidebarContent>

      {/* Admin group sits directly above the footer, not right after My
         Work - pushed down by SidebarContent's flex layout via mt-auto on
         this wrapper. */}
      {isAdmin && (
        <div className="mt-auto">
          <NavGroup label="Admin" items={ADMIN_ITEMS} />
        </div>
      )}

      {/* Profile, theme, size and sign-out live in the header's
         ProfileMenu - this footer only pads the drawer clear of the
         phone's home indicator. */}
      <SidebarFooter className="pb-[max(0.5rem,env(safe-area-inset-bottom))]" />

      {/* Drag-to-resize / click-to-toggle handle on the sidebar's edge
         (desktop only - hidden on mobile, where the Sheet drawer handles
         open/close instead). */}
      <SidebarRail />
    </Sidebar>
  );
}

/** Current page's nav label, for the phone top bar's title - the phone
 *  layout has no visible sidebar to show "where am I", so the bar says it. */
function usePageTitle(): string {
  const { pathname } = useLocation();
  if (pathname.startsWith("/my-requests/new")) return "New Request";
  if (pathname.startsWith("/other-reimbursements/new")) return "New Other Reimbursement";
  const all = [
    ...PRIMARY_ITEMS,
    ...MY_WORK_ITEMS(true),
    ...APPROVER_ITEMS,
    ...ADMIN_ITEMS
  ];
  return all.find((item) => isActivePath(pathname, item))?.label ?? "ASMARA";
}

/** Phone-only top app bar: back arrow (or the logo on the Dashboard),
 *  current page title, profile menu. Sticky above the scrolling content;
 *  hidden at md and up, where DesktopHeader and PageHeader's own back
 *  arrow take over. Main navigation lives in MobileBottomNav, not a
 *  hamburger here - bottom tabs are reachable one-handed. */
export function MobileHeader({
  user,
  onLogout
}: {
  user: AuthUser;
  onLogout: () => void;
}) {
  const title = usePageTitle();
  const back = useBackTarget();
  return (
    <header className="flex h-14 shrink-0 items-center gap-2.5 border-b bg-card/95 px-4 pt-[env(safe-area-inset-top)] backdrop-blur supports-[backdrop-filter]:bg-card/80 md:hidden">
      {back ? (
        <Button
          asChild
          variant="ghost"
          size="icon"
          className="-ml-2 h-9 w-9 shrink-0"
        >
          <Link to={back.to} title={`Back to ${back.label}`}>
            <ArrowLeft className="h-5 w-5" />
            <span className="sr-only">Back to {back.label}</span>
          </Link>
        </Button>
      ) : (
        <img
          src="/asmara-logo.png"
          alt="ASMARA"
          className="h-7 w-7 shrink-0 object-contain"
        />
      )}
      <p className="min-w-0 flex-1 truncate text-base font-semibold tracking-tight">
        {title}
      </p>
      <ProfileMenu user={user} onLogout={onLogout} className="-mr-1" />
    </header>
  );
}

/** Phone-only bottom tab bar - the everyday destinations as tabs, plus a
 *  "More" tab that opens the full sidebar drawer (the remaining pages, e.g.
 *  Admin - profile and sign out are in MobileHeader's ProfileMenu). Tabs are role-aware, same rules as the sidebar groups:
 *  My Approvals only for managers, Approver Queue only for Finance. */
export function MobileBottomNav({ user }: { user: AuthUser }) {
  const location = useLocation();
  const { openMobile, setOpenMobile } = useSidebar();
  const tabs: NavEntry[] = [
    { to: "/", end: true, icon: LayoutDashboard, label: "Home" },
    { to: "/my-requests", icon: FileText, label: "Requests" },
    ...(user.isManager
      ? [{ to: "/my-approvals", icon: CheckSquare, label: "Approvals" }]
      : []),
    ...(user.roles?.includes("Finance")
      ? [{ to: "/approver-queue", icon: Banknote, label: "Queue" }]
      : [])
  ];

  const tabClass = (active: boolean) =>
    cn(
      "flex min-w-0 flex-1 flex-col items-center justify-center  text-[0.6875rem] font-medium transition-colors",
      active ? "text-primary" : "text-muted-foreground hover:text-foreground"
    );

  return (
    <nav
      aria-label="Primary"
      className="fixed inset-x-0 bottom-0 z-40 border-t bg-card/95 pb-[env(safe-area-inset-bottom)] backdrop-blur supports-[backdrop-filter]:bg-card/80 md:hidden"
    >
      <div className="flex h-16 items-stretch">
        {tabs.map((tab) => {
          const active = !openMobile && isActivePath(location.pathname, tab);
          return (
            <NavLink
              key={tab.to}
              to={tab.to}
              end={tab.end}
              className={tabClass(active)}
            >
              <span
                className={cn(
                  "flex h-[2.75rem] w-12 items-center justify-center rounded-full transition-colors",
                  active && "bg-primary/10"
                )}
              >
                <tab.icon className="h-5 w-5" />
              </span>
              <span className="truncate">{tab.label}</span>
            </NavLink>
          );
        })}
        <button
          type="button"
          className={tabClass(openMobile)}
          onClick={() => setOpenMobile(true)}
        >
          <span
            className={cn(
              "flex h-[2.75rem] w-12 items-center justify-center rounded-full transition-colors",
              openMobile && "bg-primary/10"
            )}
          >
            <Menu className="h-5 w-5" />
          </span>
          <span>More</span>
        </button>
      </div>
    </nav>
  );
}

/** Tablet/desktop slim header carrying the collapse trigger. SidebarRail (the
 *  hover strip on the sidebar's own edge) exists too, but it's a thin,
 *  easy-to-miss hit target - this is the actual discoverable button for
 *  collapsing/expanding. The profile menu sits at its right. Hidden below
 *  md, where MobileHeader and MobileBottomNav take over instead. */
export function DesktopHeader({
  user,
  onLogout
}: {
  user: AuthUser;
  onLogout: () => void;
}) {
  return (
    <header className="hidden h-12 shrink-0 items-center border-b bg-card px-3 md:flex">
      <SidebarTrigger />
      <ProfileMenu user={user} onLogout={onLogout} className="ml-auto" />
    </header>
  );
}
